<template>
  <v-dialog
    v-model="dialog"
    width="768px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :color="buttonColor"
        rounded
      >
        <v-icon v-if="icon" left>
          {{ icon }}
        </v-icon>
        <span v-if="title">{{ title }}</span>
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="headline">{{ title }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field 
                v-model="name"
                label="Name"
                prepend-icon="short_text"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="description"
                label="Description"
                prepend-icon="mdi-text"
                rows="3"
                :counter="descriptionMax"
              ></v-textarea>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="videoLink"
                label="Video Conference Link"
                prepend-icon="mdi-video-outline"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="videoShortLink"
                label="Shareable Video Link"
                prepend-icon="mdi-link"
                hint="Attendees will always get this link so if you change the
                      video conference link, their link will still work"
                persistent-hint
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field 
                v-model="capacity"
                label="Capacity"
                :rules="capacityRules"
                prepend-icon="mdi-ticket-confirmation-outline"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field 
                v-model="location"
                label="Location"
                prepend-icon="mdi-map-marker-outline"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-switch
                v-model="requiresAcknowledgement"
                label="Requires Acknowledgement"
                inset
                prepend-icon="mdi-thumb-up-outline"
              ></v-switch>
            </v-col>
            <v-col cols="6">
              <v-switch
                v-model="hideOnSignupPage"
                label="Hide on Sign Up Page"
                inset
                prepend-icon="mdi-eye-off-outline"
              ></v-switch>
            </v-col>
            <v-col v-if="showSameDayAlert" cols="12" class="pb-0">
              <v-alert v-model="showSameDayAlert" type="warning" outlined dismissible>
                <v-row>
                  <v-col class="pt-0 pb-0" cols="12">
                    <strong>There are other sessions on this day</strong>
                  </v-col>
                  <v-col class="d-flex justfiy-center">
                    <v-list two-line>
                      <v-list-item 
                        v-for="s in sessionsOnSameDay" 
                        :key="s.id"
                        link
                        @click="openSessionInNewTab(s)"
                      >
                        <v-list-item-icon>
                          <span class="caption">
                            {{ formatTime12h(s.fields.Start) }}
                          </span>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ s.fields.Name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ s.fields['Curriculum Name'][0] }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-icon>
                          <v-icon>mdi-open-in-new</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
            <v-col cols="6">
              <v-menu
                ref="startDateMenu"
                v-model="startDateMenu"
                :close-on-content-click="false"
                :return-value.sync="startDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    prepend-icon="calendar_today"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn 
                    rounded
                    text
                    color="primary"
                    @click="startDateMenu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    rounded
                    text
                    color="primary"
                    @click="$refs.startDateMenu.save(startDate)"
                  >
                    Save
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                ref="startTimeMenu"
                v-model="startTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="startTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startTime"
                    label="Start Time"
                    prepend-icon="access_time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="startTimeMenu"
                  v-model="startTime"
                  full-width
                  @click:minute="$refs.startTimeMenu.save(startTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                ref="endDateMenu"
                v-model="endDateMenu"
                :close-on-content-click="false"
                :return-value.sync="endDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDate"
                    label="End Date"
                    prepend-icon="calendar_today"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                  scrollable
                  :min="this.startDate"
                >
                  <v-spacer></v-spacer>
                  <v-btn 
                    text
                    rounded
                    color="primary"
                    @click="endDateMenu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    text
                    rounded
                    color="primary"
                    @click="$refs.endDateMenu.save(endDate)"
                  >
                    Save
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                ref="endTimeMenu"
                v-model="endTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="endTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endTime"
                    label="End Time"
                    prepend-icon="access_time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="endTimeMenu"
                  v-model="endTime"
                  full-width
                  :min="startTime"
                  @click:minute="$refs.endTimeMenu.save(endTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn 
          v-if="showDeleteButton"
          text
          rounded
          color="error"
          @click="toggleShowDeleteConfirmation"
        >
          Delete Session
        </v-btn>
        <v-btn 
          v-if="showDeleteConfirmationButton"
          color="error"
          rounded
          @click="deleteCurriculum"
          :loading="deleteLoading"
        >
          Really Delete Session?
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
        <v-btn 
          color="blue darken-1"
          text
          rounded
          @click="save"
          :disabled="saveDisabled"
          :loading="saveLoading"
        >Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import moment from 'moment'
  
  export default {
    name: 'SessionForm',
    props: {
      title: String,
      icon: String,
      method: String,
      session: Object,
      curriculum: Object,
      buttonColor: String
    },
    data () {
      return {
        dialog: false,
        saveDisabled: false,
        saveLoading: false,
        showDeleteConfirmationButton: false,
        deleteLoading: false,
        name: '',
        description: '',
        descriptionMax: 268,
        descriptionRules: [
          value => {
            return value.length <= this.descriptionMax || 
                   'Max character limit reached'
          }
        ],
        location: '',
        videoLink: null,
        videoShortLink: null,
        capacity: null,
        capacityRules: [
          value => {
            const numbersOnly = /^\d+$|^$/
            return numbersOnly.test(value) || 'Please enter a number'
          }
        ],
        hideOnSignupPage: false,
        requiresAcknowledgement: false,
        startDate: null,
        startDateMenu: false,
        startTime: null,
        startTimeMenu: false,
        endDate: null,
        endDateMenu: false,
        endTime: null,
        endTimeMenu: false
      }
    },
    computed: {
      showDeleteButton () {
        return !this.showDeleteConfirmationButton && this.method === 'edit'
      }, 
      startDateFormatted () {
        return this.startDate ? moment(this.startDate)
                            .format('dddd, MMMM Do YYYY') : null
      },
      endDateFormatted () {
        return this.endDate ? moment(this.endDate)
                          .format('dddd MMMM Do YYYY') : null
      },
      startDatetimeFormatted () {
        if (this.startTime) {
          return moment(`${this.startDate} ${this.startTime}`).toISOString()
        } else {
          return moment(`${this.startDate}`).toISOString()
        }
      },
      endDatetimeFormatted () {
        if (this.endTime) {
          return moment(`${this.endDate} ${this.endTime}`).toISOString()
        } else {
          return moment(`${this.endDate}`).toISOString()
        }
      },
      sessions () {
        return this.$store.getters.getSessions
      },
      sessionsOnSameDay () {
        const datesAreOnSameDay = ((first, second) =>  {
          return first.getFullYear() === second.getFullYear() &&
                 first.getMonth() === second.getMonth() &&
                 first.getDate() === second.getDate() + 1;
        })
        return this.sessions.filter(s => {
          if (!s.fields.Start) return false
          if (this.session && s.id === this.session.id) return false
          return datesAreOnSameDay(new Date(s.fields.Start), new Date(this.startDate))
        }).sort((a, b) => {
          return a.fields.Start > b.fields.Start ? 1 : -1
        })
      },
      showSameDayAlert () {
        return this.sessionsOnSameDay.length > 0
      }
    },
    created () {
      if (this.session) {
        this.name = this.session.fields.Name
        this.description = this.session.fields.Description
        this.location = this.session.fields.Location
        this.videoLink = this.session.fields['Video Link']
        this.videoShortLink = this.session.fields['Video Share Link']
        this.capacity = this.session.fields.Capacity
        this.requiresAcknowledgement = this.session
                                       .fields['Requires Acknowledgement']
        this.hideOnSignupPage = this.session.fields['Hide On Signup Page']
        this.startDate = this.formatDate(this.session.fields.Start)
        this.endDate = this.formatDate(this.session.fields.End)
        this.startTime = this.formatTime(this.session.fields.Start)
        this.endTime = this.formatTime(this.session.fields.End)
      }
      if (!this.sessions || this.sessions.length === 0) {
        this.$store.dispatch('getRecords', {
          table: 'Sessions',
          view: 'All Sessions',
          searchFormula: `Start >= TODAY()`,
          dispatch: 'setSessions'
        })
      }
    },
    methods: {
      toggleShowDeleteConfirmation () {
        return this.showDeleteConfirmationButton = 
               !this.showDeleteConfirmationButton
      },
      deleteCurriculum () {
        this.deleteLoading = true
        this.$store.dispatch('deleteRecords', {
          table: 'Sessions',
          records: [this.session]
        })
        .then(() => {
          this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `Deleted ${this.name}`
          })
          this.showDeleteConfirmationButton = false
          this.deleteLoading = false
          this.$router.push({ name: 'Curriculum', params: { 
            id: this.session.fields.Curriculum[0]
          }})
        })
      },
      formatDate (date) {
        return moment(date).format('YYYY-MM-DD')
      },
      formatTime (date) {
        return moment(date).format('HH:mm')
      },
      formatTime12h (date) {
        let d = new Date(date)
        let meridian = 'am'
        if (d.getHours() >= 12) {
          if (d.getHours() !== 12) { 
            d.setHours(d.getHours() - 12)
          }
          meridian = 'pm'
        }
        let format = this.formatTime(d)
        return `${format} ${meridian}`
      },
      openSessionInNewTab (session) {
        let routeData = this.$router.resolve({ name: 'Session', params: { id: session.id }})
        window.open(routeData.href, '_blank')
      },
      reset () {
        this.name = ''
        this.description = ''
        this.location = ''
        this.videoLink = null
        this.capactiy = null
        this.hideOnSignupPage = false
        this.requiresAcknowledgement = false
        this.startDate = null
        this.startTime = null
        this.endDate = null
        this.endTime = null
      },
      async save () {
        this.saveLoading = true
        this.saveDisabled = true

        let fields = {
          'Name': this.name,
          'Description': this.description,
          'Location': this.location,
          'Video Link': this.videoLink,
          'Capacity': this.capacity ? parseInt(this.capacity) : null,
          'Hide On Signup Page': this.hideOnSignupPage,
          'Requires Acknowledgement': this.requiresAcknowledgement,
          'Start': this.startDatetimeFormatted,
          'End': this.endDatetimeFormatted
        }
        
        const vl = this.session ? this.session.fields['Video Link'] : null
        const vsl = this.session ? this.session.fields['Video Share Link'] 
                                 : null
        const endChanged = (() => {
          if (this.session) {
            return !moment(this.session.fields.End)
                   .isSame(this.endDatetimeFormatted)
          }
          if (this.endDateFormatted) {
            return true
          }
          return false
        })
        const createNewVideoShortLink = !vsl && this.videoLink
        const updateVideoShortLink = this.videoLink !== vl || endChanged
        
        if (createNewVideoShortLink) {
          console.log('we need to make a video link...')
          let linkData = {
            originalUrl: this.videoLink,
            title: this.name
          }
          if (this.endDateFormatted) {
            linkData.expiresAt = parseInt((new Date(this.endDatetimeFormatted)
                                              .getTime() / 1000).toFixed(0))
          }
          let videoShortLink = await this.$store
                                     .dispatch('createShortLink', linkData)
          fields['Video Share Link'] = videoShortLink.shortURL
          fields['Video Share Link ID'] = videoShortLink.id
        }
        
        if (this.method === 'create') {
          fields.Curriculum = [this.curriculum.id]
          this.$store.dispatch('createRecords', {
            table: 'Sessions',
            records: [{ fields: fields }]
          })
          .then((values) => {
            this.$store.dispatch('addSession', values[0].data[0])
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Created ${this.name}`
            })
            this.saveLoading = false
            this.saveDisabled = false
            this.close()
          })
        } else if (this.method === 'edit') {
          this.$store.dispatch('updateRecords', {
            table: 'Sessions',
            records: [{ id: this.session.id, fields: fields }]
          })
          .then(() => {
            if (updateVideoShortLink && !createNewVideoShortLink) {
              console.log('we need to update the video link destination...')
              const linkId = this.session.fields['Video Share Link ID']
              let linkData = {
                linkId: linkId,
                originalUrl: this.videoLink
              }
              if (this.endDateFormatted) {
                linkData.expiresAt = parseInt((new Date(this.endDatetimeFormatted)
                                                   .getTime() / 1000).toFixed(0))
              }
              this.$store.dispatch('updateShortLinkDestination', linkData)
            }
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Updated ${this.name}`
            })
            this.$store.dispatch('getRecords', {
              table: 'Sessions',
              view: 'All Sessions',
              dispatch: 'setSessions'
            })
            this.saveLoading = false
            this.saveDisabled = false
            this.close()
          })
        }
      },
      close () {
        this.dialog = false
        if (this.method === 'create') {
          this.reset()
        }
      }
    }
  }
</script>
